import {
  EWarehouseCategorySuggestionKind,
  type IAdminCategory,
  type IWarehouseCategoryInStock,
  type IWarehouseCategorySuggestion,
} from '@mahawi/eshop-common/src/types';
import { type IAICommunicatorWarehouseCategorySuggestionProposedCategory } from '@mahawi/eshop-rabbit';
import { createSlice } from '@reduxjs/toolkit';

import {
  asyncStateInitial,
  finishLoading,
  requestFailedMatcher,
  resetLoadingOnRequestFailed,
  startLoading,
} from '../async-state';
import {
  EWarehouseCategorySuggestionItemStatus,
  EWarehouseCategorySuggestionsRunStatus,
  type ICategoriesState,
  type IWarehouseCategorySuggestionItem,
} from './types';

export const preloadedState: ICategoriesState = {
  ...asyncStateInitial,
  categories: undefined,
  likelyProductCategories: {
    uuids: undefined,
  },
  warehouseCategorySuggestions: {
    stage: EWarehouseCategorySuggestionKind.PAIR_EXISTING,
    status: EWarehouseCategorySuggestionsRunStatus.IDLE,
    items: [],
    suggestions: undefined,
    batch: {
      current: 0,
      total: 0,
    },
    failedBatchesCount: 0,
    cachedCount: 0,
    startedAt: undefined,
    finishedAt: undefined,
    applyingUuids: [],
  },
};

const setItemsStatus = (
  items: IWarehouseCategorySuggestionItem[],
  uuids: string[],
  getStatus: (uuid: string) => EWarehouseCategorySuggestionItemStatus,
): void => {
  items.forEach((item: IWarehouseCategorySuggestionItem): void => {
    if (uuids.includes(item.uuid)) {
      item.status = getStatus(item.uuid);
    }
  });
};

const sortByPosition = (categories: IAdminCategory[]): IAdminCategory[] =>
  [...categories].sort(
    (a: IAdminCategory, b: IAdminCategory): number => a.position - b.position,
  );

const categoriesSlice = createSlice({
  name: 'Categories',
  initialState: preloadedState,
  reducers: {
    categoryAdd: {
      reducer(state) {
        startLoading(state);
      },
      prepare(
        name: string,
        languageTypeCode: string,
        parentCategoryUuid?: string,
      ) {
        return {
          payload: {
            name,
            languageType: { code: languageTypeCode },
            parentCategoryUuid,
          },
        };
      },
    },
    categoriesLoadAll(state) {
      startLoading(state);
    },
    categoriesLoadAllResponse: {
      reducer(state, action: { payload: { categories: IAdminCategory[] } }) {
        state.categories = sortByPosition(action.payload.categories);
        finishLoading(state);
      },
      prepare(categories: IAdminCategory[]) {
        return { payload: { categories } };
      },
    },
    getLikelyProductCategories: {
      reducer(state) {
        startLoading(state);
        state.likelyProductCategories.uuids = undefined;
      },
      prepare(uuids: string[]) {
        return { payload: { category: { uuids } } };
      },
    },
    getLikelyProductCategoriesResponse: {
      reducer(state, action: { payload: { category: { uuids: string[] } } }) {
        state.likelyProductCategories.uuids = action.payload.category.uuids;
        finishLoading(state);
      },
      prepare(uuids: string[]) {
        return { payload: { category: { uuids } } };
      },
    },
    categoriesWarehouseSuggestionsStart: {
      reducer(
        state,
        action: { payload: { stage: EWarehouseCategorySuggestionKind } },
      ) {
        state.warehouseCategorySuggestions = {
          ...preloadedState.warehouseCategorySuggestions,
          stage: action.payload.stage,
          status: EWarehouseCategorySuggestionsRunStatus.PREPARING,
          suggestions: [],
          startedAt: Date.now(),
        };
      },
      prepare({
        languageTypeCode,
        stage,
        skipCache = false,
      }: {
        languageTypeCode: string;
        stage: EWarehouseCategorySuggestionKind;
        skipCache?: boolean;
      }) {
        return {
          payload: {
            languageType: { code: languageTypeCode },
            stage,
            skipCache,
          },
        };
      },
    },
    categoriesWarehouseSuggestionsPrepare(state) {
      state.warehouseCategorySuggestions.status =
        EWarehouseCategorySuggestionsRunStatus.PREPARING;
    },
    categoriesWarehouseSuggestionsPrepareResponse: {
      reducer(
        state,
        action: {
          payload: {
            warehouseCategories: IWarehouseCategoryInStock[];
            batchesCount: number;
          };
        },
      ) {
        const { warehouseCategories, batchesCount } = action.payload;

        state.warehouseCategorySuggestions.status =
          EWarehouseCategorySuggestionsRunStatus.RUNNING;
        state.warehouseCategorySuggestions.items = warehouseCategories.map(
          (
            warehouseCategory: IWarehouseCategoryInStock,
          ): IWarehouseCategorySuggestionItem => ({
            uuid: warehouseCategory.uuid,
            name: warehouseCategory.name || warehouseCategory.rawName,
            inStockProductsCount: warehouseCategory.inStockProductsCount,
            status: EWarehouseCategorySuggestionItemStatus.PENDING,
          }),
        );
        state.warehouseCategorySuggestions.batch = {
          current: 0,
          total: batchesCount,
        };
      },
      prepare(
        warehouseCategories: IWarehouseCategoryInStock[],
        batchesCount = 0,
      ) {
        return { payload: { warehouseCategories, batchesCount } };
      },
    },
    categoriesWarehouseSuggestionsBatchStart: {
      reducer(
        state,
        action: {
          payload: { warehouseCategoryUuids: string[]; batchIndex: number };
        },
      ) {
        const { warehouseCategoryUuids, batchIndex } = action.payload;

        state.warehouseCategorySuggestions.batch.current = batchIndex + 1;
        setItemsStatus(
          state.warehouseCategorySuggestions.items,
          warehouseCategoryUuids,
          (): EWarehouseCategorySuggestionItemStatus =>
            EWarehouseCategorySuggestionItemStatus.PROCESSING,
        );
      },
      prepare(warehouseCategoryUuids: string[], batchIndex: number) {
        return { payload: { warehouseCategoryUuids, batchIndex } };
      },
    },
    categoriesWarehouseSuggestionsBatchLoad: {
      reducer() {},
      prepare({
        languageTypeCode,
        stage,
        warehouseCategories,
        proposedCategories = [],
        skipCache = false,
      }: {
        languageTypeCode: string;
        stage: EWarehouseCategorySuggestionKind;
        warehouseCategories: IWarehouseCategoryInStock[];
        proposedCategories?: IAICommunicatorWarehouseCategorySuggestionProposedCategory[];
        skipCache?: boolean;
      }) {
        return {
          payload: {
            languageType: { code: languageTypeCode },
            stage,
            warehouseCategories,
            proposedCategories,
            skipCache,
          },
        };
      },
    },
    categoriesWarehouseSuggestionsBatchLoadResponse: {
      reducer(
        state,
        action: {
          payload: {
            warehouseCategoryUuids: string[];
            suggestions: IWarehouseCategorySuggestion[];
            cachedCount: number;
          };
        },
      ) {
        const { warehouseCategoryUuids, suggestions, cachedCount } =
          action.payload;

        state.warehouseCategorySuggestions.cachedCount += cachedCount;
        const suggestedUuids: string[] = suggestions.map(
          (suggestion: IWarehouseCategorySuggestion): string =>
            suggestion.warehouseCategory.uuid,
        );

        setItemsStatus(
          state.warehouseCategorySuggestions.items,
          warehouseCategoryUuids,
          (uuid: string): EWarehouseCategorySuggestionItemStatus =>
            suggestedUuids.includes(uuid)
              ? EWarehouseCategorySuggestionItemStatus.SUGGESTED
              : EWarehouseCategorySuggestionItemStatus.NO_SUGGESTION,
        );
        state.warehouseCategorySuggestions.suggestions = [
          ...(state.warehouseCategorySuggestions.suggestions ?? []),
          ...suggestions,
        ];
      },
      prepare(
        warehouseCategoryUuids: string[],
        suggestions: IWarehouseCategorySuggestion[],
        cachedCount = 0,
      ) {
        return {
          payload: { warehouseCategoryUuids, suggestions, cachedCount },
        };
      },
    },
    categoriesWarehouseSuggestionsBatchFailed: {
      reducer(
        state,
        action: { payload: { warehouseCategoryUuids: string[] } },
      ) {
        state.warehouseCategorySuggestions.failedBatchesCount += 1;
        setItemsStatus(
          state.warehouseCategorySuggestions.items,
          action.payload.warehouseCategoryUuids,
          (): EWarehouseCategorySuggestionItemStatus =>
            EWarehouseCategorySuggestionItemStatus.FAILED,
        );
      },
      prepare(warehouseCategoryUuids: string[]) {
        return { payload: { warehouseCategoryUuids } };
      },
    },
    categoriesWarehouseSuggestionsFinish(state) {
      state.warehouseCategorySuggestions.status =
        EWarehouseCategorySuggestionsRunStatus.DONE;
      state.warehouseCategorySuggestions.finishedAt = Date.now();
    },
    categoriesWarehouseSuggestionsCancel(state) {
      state.warehouseCategorySuggestions.status =
        EWarehouseCategorySuggestionsRunStatus.CANCELLED;
      state.warehouseCategorySuggestions.finishedAt = Date.now();
      state.warehouseCategorySuggestions.items.forEach(
        (item: IWarehouseCategorySuggestionItem): void => {
          if (
            item.status === EWarehouseCategorySuggestionItemStatus.PROCESSING
          ) {
            item.status = EWarehouseCategorySuggestionItemStatus.PENDING;
          }
        },
      );
    },
    categoriesWarehouseSuggestionsFailed(state) {
      state.warehouseCategorySuggestions.status =
        EWarehouseCategorySuggestionsRunStatus.FAILED;
      state.warehouseCategorySuggestions.finishedAt = Date.now();
    },
    categoryWarehouseSuggestionApply: {
      reducer(
        state,
        action: { payload: { warehouseCategoryUuids: string[] } },
      ) {
        state.warehouseCategorySuggestions.applyingUuids.push(
          ...action.payload.warehouseCategoryUuids,
        );
      },
      prepare(
        suggestion: IWarehouseCategorySuggestion,
        warehouseCategoryUuids: string[],
        languageTypeCode: string,
      ) {
        return {
          payload: {
            suggestion,
            warehouseCategoryUuids,
            languageType: { code: languageTypeCode },
          },
        };
      },
    },
    categoryWarehouseSuggestionApplyResponse: {
      reducer(
        state,
        action: {
          payload: {
            warehouseCategoryUuids: string[];
            categories: IAdminCategory[];
          };
        },
      ) {
        const { warehouseCategoryUuids, categories } = action.payload;

        state.warehouseCategorySuggestions.applyingUuids =
          state.warehouseCategorySuggestions.applyingUuids.filter(
            (uuid: string): boolean => !warehouseCategoryUuids.includes(uuid),
          );
        state.warehouseCategorySuggestions.suggestions =
          state.warehouseCategorySuggestions.suggestions?.filter(
            (suggestion: IWarehouseCategorySuggestion): boolean =>
              !warehouseCategoryUuids.includes(
                suggestion.warehouseCategory.uuid,
              ),
          );
        state.categories = sortByPosition(categories);
        state.updatedAt = Date.now();
      },
      prepare(warehouseCategoryUuids: string[], categories: IAdminCategory[]) {
        return { payload: { warehouseCategoryUuids, categories } };
      },
    },
    categoryWarehouseSuggestionApplyRejected: {
      reducer(
        state,
        action: { payload: { warehouseCategoryUuids: string[] } },
      ) {
        state.warehouseCategorySuggestions.applyingUuids =
          state.warehouseCategorySuggestions.applyingUuids.filter(
            (uuid: string): boolean =>
              !action.payload.warehouseCategoryUuids.includes(uuid),
          );
      },
      prepare(warehouseCategoryUuids: string[]) {
        return { payload: { warehouseCategoryUuids } };
      },
    },
    categoryWarehouseSuggestionDismiss: {
      reducer(
        state,
        action: { payload: { warehouseCategory: { uuid: string } } },
      ) {
        state.warehouseCategorySuggestions.suggestions =
          state.warehouseCategorySuggestions.suggestions?.filter(
            (suggestion: IWarehouseCategorySuggestion): boolean =>
              suggestion.warehouseCategory.uuid !==
              action.payload.warehouseCategory.uuid,
          );
      },
      prepare(warehouseCategoryUuid: string) {
        return {
          payload: { warehouseCategory: { uuid: warehouseCategoryUuid } },
        };
      },
    },
  },
  extraReducers: (builder) => {
    builder.addMatcher(requestFailedMatcher('Categories'), (state): void => {
      resetLoadingOnRequestFailed(state);
      state.warehouseCategorySuggestions.applyingUuids = [];
    });
  },
});

export const {
  categoryAdd,
  categoriesLoadAll,
  categoriesLoadAllResponse,
  getLikelyProductCategories,
  getLikelyProductCategoriesResponse,
  categoriesWarehouseSuggestionsStart,
  categoriesWarehouseSuggestionsPrepare,
  categoriesWarehouseSuggestionsPrepareResponse,
  categoriesWarehouseSuggestionsBatchStart,
  categoriesWarehouseSuggestionsBatchLoad,
  categoriesWarehouseSuggestionsBatchLoadResponse,
  categoriesWarehouseSuggestionsBatchFailed,
  categoriesWarehouseSuggestionsFinish,
  categoriesWarehouseSuggestionsCancel,
  categoriesWarehouseSuggestionsFailed,
  categoryWarehouseSuggestionApply,
  categoryWarehouseSuggestionApplyResponse,
  categoryWarehouseSuggestionApplyRejected,
  categoryWarehouseSuggestionDismiss,
} = categoriesSlice.actions;

export default categoriesSlice.reducer;
